// Market product pricing — the ONE place the preview turns stored CNY prices
// into what a trader sees (owner pricing audit, 2026-10-19).
//
// Data model (verified against HioBuy's raw payloads):
//   • every price is display_amount based (see supabase/functions/
//     hiobuy-gateway/pricing.ts for why promotions are never shown);
//   • a product is priced EITHER by quantity — several tiers, one price for all
//     SKUs — OR by SKU — each SKU its own price, the single tier being only the
//     minimum order. No product in the batch is both.
//
// Arithmetic rule: the unit price is converted and rounded to halalas FIRST,
// and the total is that displayed unit × quantity — so the numbers on the page
// always multiply out. (Before: the unit was rounded for display but the total
// was computed from the unrounded value, and large totals were rounded to whole
// riyals: 1.2345 × 1000 showed "1.23" and "1,235".)
//
// The conversion is MAABAR's admin rates (CNY → USD → SAR), passed in as
// `rates` — the same rates the rest of the catalogue uses.
//
// HioBuy's procurement fee IS in every displayed price (owner, 2026-10-19):
// it is an underlying cost of buying the goods, so a trader sees one product
// price that already carries it — never a separate line, never HioBuy's name.
// MAABAR's own Service Fee is NOT here: it appears only at checkout
// (docs/HIOBUY_MARKET_PRICING_POLICY.md). Stored prices stay the supplier's
// raw CNY; the fee is applied here, once, before conversion and rounding.

import { cnyToSar } from './displayCurrency';

export const PROCUREMENT_FEE_RATE = 0.05;
const EPS = 0.005;

/** Supplier CNY → displayed SAR: + procurement fee, converted, rounded to halalas. null for no price. */
export function toSar(cny, rates) {
  const n = Number(cny);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(cnyToSar(n * (1 + PROCUREMENT_FEE_RATE), rates) * 100) / 100;
}

export function formatSar(sar) {
  if (sar == null || !Number.isFinite(sar)) return null;
  return `${sar.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ر.س`;
}

/** Tiers sorted ascending, positive only. */
export function cleanTiers(tiers) {
  return (Array.isArray(tiers) ? tiers : [])
    .filter((t) => t && Number(t.min_quantity) > 0 && Number(t.price_cny) > 0)
    .map((t) => ({ min_quantity: Number(t.min_quantity), price_cny: Number(t.price_cny) }))
    .sort((a, b) => a.min_quantity - b.min_quantity);
}

/** The quantity the supplier's first price tier starts at (0 when there are no tiers). */
export const firstTierQty = (product) => cleanTiers(product?.price_tiers)[0]?.min_quantity ?? 0;

/**
 * 'quantity' — several tiers and every SKU at the product's base price: the
 *              tier for the quantity is the unit price.
 * 'sku'      — otherwise: the selected SKU's own price, whatever the quantity.
 */
export function pricingMode(product, skus) {
  const tiers = cleanTiers(product?.price_tiers);
  if (tiers.length < 2) return 'sku';
  const base = Number(product?.price_cny);
  const priced = (skus || []).map((s) => Number(s.price_cny)).filter((x) => x > 0);
  return priced.every((x) => Math.abs(x - base) < EPS) ? 'quantity' : 'sku';
}

/** The tier that applies to `qty`: the highest min_quantity ≤ qty (the first one below it). */
export function tierFor(tiers, qty) {
  const list = cleanTiers(tiers);
  if (!list.length) return null;
  let hit = list[0];
  for (const t of list) if (qty >= t.min_quantity) hit = t;
  return hit;
}

/**
 * Saving of each tier against the FIRST tier, in whole percent — computed from
 * the supplier's own tier prices, never a discount of ours. 0 for the first
 * tier and for anything under 1%.
 */
export function tierSavings(tiers) {
  const list = cleanTiers(tiers);
  const base = list[0]?.price_cny;
  return list.map((t) => {
    const pct = base ? Math.floor((1 - t.price_cny / base) * 100) : 0;
    return { ...t, savingPct: pct >= 1 ? pct : 0 };
  });
}

/** The ordering increment: market_products.order_step (1688 batchNumber; migration 20261019161306). */
export function orderStepOf(product) {
  const n = Math.floor(Number(product?.order_step) || 1);
  return n >= 1 ? n : 1;
}

/** The first tier ABOVE the one that applies to `qty` (the supplier's own break), or null. */
export function nextTier(tiers, qty) {
  return cleanTiers(tiers).find((t) => t.min_quantity > qty) || null;
}

/**
 * Everything the page shows for one SKU and quantity.
 *   unitCny             the supplier's unit price (raw CNY, no fee)
 *   unitSar             the displayed unit price (fee included, SAR, halalas)
 *   totalSar            unitSar × qty
 *   tier                the applied tier (quantity mode) or null
 *   moq                 the effective minimum: the largest of the product and SKU
 *                       minimum and the first price tier's quantity, rounded
 *                       UP to a multiple of the ordering step
 *   step                the ordering increment (orderStepOf: 1688 batchNumber).
 *                       Verified by order preview 2026-10-19: with step 10,
 *                       13 / 15 / 25 are rejected (MOQ_NOT_MET), 10 / 20 / 30 accepted
 *                       — and a stated MOQ of 1 with step 10 really means 10.
 *   stepOk              qty is a multiple of step
 *
 * `tierQty` is the quantity the TIER is chosen by — by default `qty`. In a cart
 * it is the product's total across all its variants. Order preview, 2026-10-19:
 *   • tier: 3 + 3 of two variants of 1068407458674 (31.8 below 5, 26.8 from 5)
 *     were priced at 26.8 each — the product total chose the tier;
 *   • minimum: 3 + 3 of 1006686643944 (MOQ 5, no step) was ACCEPTED — the
 *     minimum is met by the product total too (marketCart checks it there);
 *   • step: 2 + 3 of 534035860803 (batchNumber 5) was refused although the
 *     total is 5 — every variant must itself be a multiple of the step.
 * (A first test, 3 + 3 of 534035860803, was refused and read as "minimum per
 * variant"; that product also has step 5, so it proved nothing about the
 * minimum. Corrected the same day by the two tests above.)
 */
export function quote(product, skus, sku, qty, rates, tierQty = qty) {
  const mode = pricingMode(product, skus);
  const tier = mode === 'quantity' ? tierFor(product.price_tiers, Math.max(Number(qty) || 0, Number(tierQty) || 0)) : null;
  const skuPrice = sku ? Number(sku.price_cny) : NaN;
  const unitCny = tier ? tier.price_cny : (skuPrice > 0 ? skuPrice : Number(product?.price_cny) || null);
  const unitSar = toSar(unitCny, rates);
  const step = orderStepOf(product);
  // The supplier's first price tier is a minimum too: 1688 refuses fewer
  // (order preview 2026-10-20, 1688 product with MOQ 1 and tiers from 3:
  // 1 and 2 → PREVIEW_PRICE_UNAVAILABLE, 3 → accepted).
  const moq = Math.ceil(Math.max(1, Number(product?.moq) || 1, Number(sku?.moq) || 0, firstTierQty(product)) / step) * step;
  const q = Math.max(0, Math.floor(Number(qty) || 0));
  return {
    mode,
    tier,
    moq,
    step,
    stepOk: q % step === 0,
    unitCny,
    unitSar,
    totalSar: unitSar != null && q > 0 ? Math.round(unitSar * q * 100) / 100 : null,
  };
}

/** Card / headline range: the SKU price range, in SAR. */
export function rangeText(minCny, maxCny, rates) {
  const lo = toSar(minCny, rates);
  if (lo == null) return null;
  const hi = toSar(maxCny, rates);
  return hi != null && hi > lo ? `${formatSar(lo).replace(' ر.س', '')} – ${formatSar(hi)}` : formatSar(lo);
}
